import {ChangeEvent, FC, ReactNode, useEffect, useRef} from 'react';
import {Box} from '@mui/material';
import {gridClasses} from '@mui/x-data-grid';
import {TablePagination} from 'src/components/tables/TablePagination';
import {TableRowCounter} from 'src/components/tables/TableRowCounter';
import {tableLayoutSx} from './TableLayout.sx';

interface TableLayoutProps {
    /** Content rendered above the card (search, filter button, export, create button, etc.) */
    actions?: ReactNode;
    /** Main table body — usually a <ResponsiveTable /> */
    children: ReactNode;
    /**
     * Rendered inside the card, above the table.
     *
     * `actions` sits outside the card, which is right for a page heading and wrong for a filter bar:
     * a band of controls that belongs to this table should be bounded by the same card, sharing its
     * top corners and its rules.
     */
    toolbar?: ReactNode;
    /** Custom footer — if omitted, default pagination+counter is rendered */
    footer?: ReactNode;
    /** Hide the default footer entirely */
    hideFooter?: boolean;
    /** Drop the card's border, radius and shadow — for a table already inside a card. */
    flush?: boolean;
    /**
     * Whether the footer keeps its own padding.
     *
     * Off when the footer is one full-width action rather than a pager: the padding would leave a dead
     * border around the control, so only the middle of the strip would answer a click. The same shape
     * the charger's "add schedule" row has.
     */
    footerPadded?: boolean;

    // Default footer pagination props
    totalPages?: number;
    page?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const TableLayout: FC<TableLayoutProps> = ({
    actions,
    toolbar,
    children,
    footer,
    hideFooter = false,
    flush = false,
    footerPadded = true,
    totalPages,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
}) => {
    const shouldRenderDefaultFooter =
        !hideFooter &&
        !footer &&
        onPageChange &&
        onPageSizeChange &&
        typeof totalPages === 'number' &&
        typeof page === 'number' &&
        typeof pageSize === 'number';

    const cardRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const shownPage = useRef(page);

    // A new page starts at its first row. The rows are replaced in place, and neither the grid's own
    // scroller nor the page moves by itself, so turning a page at the bottom of the list opened the
    // next one at its bottom too. The page is scrolled only when the card's top has left the screen -
    // on a phone, where the list is part of the page - and otherwise left where the reader put it.
    useEffect(() => {
        if (shownPage.current === page) {
            return;
        }
        shownPage.current = page;

        tableRef.current?.querySelector<HTMLElement>(`.${gridClasses.virtualScroller}`)?.scrollTo({top: 0});

        const card = cardRef.current;
        if (card && card.getBoundingClientRect().top < 0) {
            card.scrollIntoView({block: 'start'});
        }
    }, [page]);

    return (
        <Box sx={toolbar ? {...tableLayoutSx.root, ...tableLayoutSx.rootWithToolbar} : tableLayoutSx.root}>
            {actions}
            <Box ref={cardRef} sx={flush ? tableLayoutSx.cardFlush : tableLayoutSx.card}>
                {toolbar}
                <Box ref={tableRef} sx={tableLayoutSx.tableWrapper}>{children}</Box>
                {footer && (
                    <Box sx={footerPadded ? tableLayoutSx.footer : tableLayoutSx.footerFlush}>
                        {footer}
                    </Box>
                )}
                {shouldRenderDefaultFooter && (
                    <Box sx={tableLayoutSx.footer}>
                        <TableRowCounter
                            defaultValue={pageSize}
                            handleChangeRowsNumber={onPageSizeChange}
                        />
                        <TablePagination
                            totalPages={totalPages}
                            page={page}
                            handleChangePage={onPageChange}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};